import React, {useCallback, useEffect, useMemo, useState} from "react";
import {DefaultPaginationInfo} from "../../../../../../../core/constants/enums";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import Button from "../../../../../../components/base/button/button";
import {Add, OpenInNew} from "@mui/icons-material";
import {Box, Paper, Tooltip} from "@mui/material";
import IconButton from "../../../../../../components/base/button/icon_button";
import {Switch} from "../../../../../../components/base/switch";
import TagManagementSearchSection from "../../../../../../components/search-sections/tag-management-list";
import {routeFunctions} from "../../../../../../../core/models/static/routes/app-routes";
import CreateNewTagsDialog from "../../../../../../components/dialogs/create-new-tags";
import LazySvg from "../../../../../../components/app-specific/lazy-svg";
import LazyImage from "../../../../../../components/app-specific/lazy-image";
import Api from "../../../../../../../core/services/api/api";
import ManageTagIconDialog from "../../../../../../components/dialogs/manage-tag-icon";
import {dataGridCellTextProps, DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../../../containers/data-grid";
import {TagManagementListDataGridColumnNames} from "../../../../../../../core/constants/table/data-grid-column-keys";
import useSearch from "../../../../../../hooks/use-search";
import {useConfigureNotebook} from "../../../../../../hooks/use-notebook-config";
import useIndustry from "../../../../../../hooks/use-industry";
import {Link} from "react-router-dom";
import useBehavioralAnalyticsTool from "../../../../../../hooks/use-behavioural-analytics-tool";
import UIUtils from "../../../../../../utils";

const initAddNewTagsDialog = {open: false};

const initChangeTagIconDialog = {open: false, tag: null, update: () => false};

const dataGridSaveConfig = {
    name: 'ProductsTagManagementListViewDataGrid',
    version: 1,
}


/**
 * Available values for the tag visibility statuses in the application
 */
export const TagVisibilities = {
    all: 'all',
    visible: 'visible',
    invisible: 'invisible',
}

/**
 * Available values for the tag searchability statuses in the application
 */
export const TagSearchAbilities = {
    all: 'all',
    searchable: 'searchable',
    notSearchable: 'notSearchable',
}

/**
 * Available values for the tag share statuses in the application
 */
export const TagShareStatuses = {
    all: 'all',
    shared: 'shared',
    private: 'private',
}

const ProductsTagManagementListView = () => {
    const {industries} = useIndustry({noCategories: true})
    const [data, setData] = useState([]);
    const [addNewTagsDialog, setAddNewTagsDialog] = useState(initAddNewTagsDialog);
    const [changeTagIconDialog, setChangeTagIconDialog] = useState(initChangeTagIconDialog);
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('tag-management-list-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.productsTagsList);
        configureBehavioralTool((tags) => tags.productsTagsList);
    }, [configureBehavioralTool, configureNotebookTags]);

    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                name: TagManagementListDataGridColumnNames.view,
                sortable: false,
                width: 0,
            },
            {
                title: "Title",
                name: TagManagementListDataGridColumnNames.title,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 200,
            },
            {
                title: "Visible for Client",
                name: TagManagementListDataGridColumnNames.isVisible,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 150,
            },
            {
                title: "Searchable",
                name: TagManagementListDataGridColumnNames.isSearchable,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
            },
            {
                title: "Public",
                name: TagManagementListDataGridColumnNames.isPublic,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
            },
            {
                name: TagManagementListDataGridColumnNames.changeIcon,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 130,
                resizable: false,
            },
            {
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                name: TagManagementListDataGridColumnNames.delete,
                sortable: false,
                resizable: false,
            }
        ], [])

    const [dataGridColumns, , dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    })

    /**
     * Determines the visibility value of the search data based on the filter values.
     */
    const determineVisibility = useCallback((args) => {
        switch (args.filters?.visibility) {
            case TagVisibilities.visible:
                return true;
            case TagVisibilities.invisible:
                return false;
            default:
                return null;
        }
    }, [])

    /**
     * Determines the searchability value of the search data based on the filter values.
     */
    const determineSearchability = useCallback((args) => {
        switch (args.filters?.searchAbility) {
            case TagSearchAbilities.searchable:
                return true;
            case TagSearchAbilities.notSearchable:
                return false;
            default:
                return null;
        }
    }, [])

    /**
     * Determines the share status value of the search data based on the filter values.
     * @return {null|boolean}
     */
    const determineShareStatus = useCallback((args) => {
        switch (args.filters?.shareStatus) {
            case TagShareStatuses.shared:
                return true;
            case TagShareStatuses.private:
                return false;
            default:
                return null;
        }
    }, [])

    /**
     * Searches for tags in the system:
     * - Calls the api regarding this action
     * - if the api call was successful, updates the pagination information based on the received value
     * @type {(function(UseSearchFunctionArg, boolean=): Promise<undefined|ApiResponseDs<*>>)}
     */
    const fetchEntries = useCallback(async (args) => {
        const forApi = {
            keyword: args.filters?.keyword?.toString(),
            isVisible: determineVisibility(args),
            isSearchable: determineSearchability(args),
            industryId: args.filters?.industryId,
            isPublic: determineShareStatus(args),
            paginationDs: {
                pageSize: args.paginationInfo.pageSize,
                currentPage: args.paginationInfo.currentPage,
            },
        };
        const response = await Api.searchTags(forApi, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setData(response?.data?.items?.map(e => ({
                ...e,
                updatingVisibility: false,
                updatingSearchable: false,
                updatingIsPublic: false,
                removing: false,
                iconFileName: !e?.iconFileName
                    ? ''
                    : response?.configuration?.TagIconUrlBaseAddress?.concat(e?.iconFileName ?? ''),
            })) ?? []);
            args.setPaginationInfo(prevState => ({
                ...prevState,
                length: response?.data?.totalCount ?? 0
            }))
        }
    }, [isMounted, determineVisibility, determineSearchability, determineShareStatus])

    /**
     * Searches among the entries based on the filters from the user.
     * @type {UseSearchFunction}
     */
    const search = useCallback(async (result) => {
        await fetchEntries(result);
    }, [fetchEntries]);

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize
    } : DefaultPaginationInfo, [dataGridPageSize])

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
    }), [initialPaginationInfo]);

    const searchData = useSearch(search, initialSearchDataSettings)

    /**
     * Sets the value of the given tag property [key] for the tag that is associated with the given tagId in the
     * data list state.
     * @type {(function(string, string, any): void)}
     */
    const setTagProperty = useCallback((tagId, key, value) => {
        setData(prevState => prevState?.map(e => e.id === tagId
            ? {...e, [key]: value}
            : e
        ) ?? [])
    }, [])


    /**
     * Toggles the visibility of the given tag with the given value in the server.
     *
     * * if the response of the api call is successful, sets the component state.
     * @type {(function(any, boolean): Promise<void>)}
     */
    const toggleTagVisibility = useCallback(async (tag, value) => {
        setTagProperty(tag?.id, 'updatingVisibility', true);
        const forApi = {
            id: tag?.id,
            isVisibleForClient: value,
        }
        const response = await Api.updateTagInformation(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setTagProperty(tag?.id, 'isVisibleForClient', value);
        }
        setTagProperty(tag?.id, 'updatingVisibility', false);
    }, [isMounted, setTagProperty])

    /**
     * Toggles the isPublic status of the given tag with the given value in the server.
     *
     * * if the response of the api call is successful, sets the component state.
     * @type {(function(any, boolean): Promise<void>)}
     */
    const toggleTagIsPublicStatus = useCallback(async (tag, value) => {
        setTagProperty(tag?.id, 'updatingIsPublic', true);
        const forApi = {
            id: tag?.id,
            isPublic: value,
        }
        const response = await Api.updateTagInformation(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setTagProperty(tag?.id, 'isPublic', value);
        }
        setTagProperty(tag?.id, 'updatingIsPublic', false);
    }, [isMounted, setTagProperty])

    /**
     * Toggles the isSearchable status of the given tag with the given value in the server.
     *
     * * if the response of the api call is successful, sets the component state.
     * @type {(function(any, boolean): Promise<void>)}
     */
    const toggleTagSearchAbility = useCallback(async (tag, value) => {
        setTagProperty(tag?.id, 'updatingSearchable', true);
        const forApi = {
            id: tag?.id,
            searchable: value,
        }
        const response = await Api.updateTagInformation(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setTagProperty(tag?.id, 'searchable', value);
        }
        setTagProperty(tag?.id, 'updatingSearchable', false);
    }, [isMounted, setTagProperty])

    /**
     * Deletes the tag from the system:
     * - Calls the api regarding this action
     * - Removes the selected tag from the [data]
     * @type {(function(any): Promise<void>)}
     */
    const deleteTag = useCallback(async (tag) => {
        setTagProperty(tag?.id, 'removing', true);
        const forApi = {
            ids: [tag?.id],
        }
        const response = await Api.deleteTagsFromSystem(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setData(prevState => prevState?.filter(e => e.id !== tag?.id))
        } else {
            setTagProperty(tag?.id, 'removing', false);
        }
    }, [isMounted, setTagProperty])

    /**
     * Handles showing the confirmation dialog for getting the deletion confirmation:
     * if the user response was confirmation, calls the function for removing the tag
     * @type {(function(any): void)}
     */
    const getTagDeletionConfirmation = useCallback((tag) => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    Do you want to delete the selected tag?
                </p>
            </div>,
            {},
            (decision) => decision && deleteTag(tag)
        );
    }, [deleteTag])

    /**
     * Adds the newly defined tags of the system to the table entries.
     * @param {any[]} tags
     */
    const addNewlyDefinedTags = useCallback((tags) => {
        setData(prevState => [
            ...tags?.map(e => ({
                ...e,
                updatingVisibility: false,
                updatingSearchable: false,
                updatingIsPublic: false,
                removing: false,
            })) ?? [],
            ...(prevState ?? []),
        ])
    }, [])

    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() => {
        return data.map(e => {
            const industry = industries?.find(i => i.id === e.industryId);
            return ({
                key: e.id,
                data: e,
                cells: {
                    [TagManagementListDataGridColumnNames.view]:
                        <IconButton
                            color={'secondary'}
                            component={Link}
                            to={routeFunctions.products.tags.single(e.id)}
                        >
                            <OpenInNew className='icon normal'/>
                        </IconButton>,
                    [TagManagementListDataGridColumnNames.title]: {
                        value: <div className={'tag-container'}>
                                <div className={'tag-icon-container'}>
                                    {
                                        !!industry && industries.length > 1 &&
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: 1,
                                            color: 'primary.main',
                                            marginRight: 1,
                                        }}>
                                            {
                                                React.cloneElement(UIUtils.getIndustryIcon(industry?.iconFileName, industry?.id, true) ??
                                                    <div/>, {style: {width: 20, height: 20, color: 'inherit'}})
                                            }
                                        </Box>
                                    }
                                    {
                                        e?.iconFileName && (
                                            e?.iconFileName?.toLowerCase()?.endsWith('.png')
                                                ? <LazyImage
                                                    src={e?.iconFileName}
                                                    height={30}
                                                    width={30}
                                                />
                                                : <LazySvg
                                                    src={e?.iconFileName}
                                                    height={30}
                                                    width={30}
                                                />
                                        )
                                    }
                                </div>
                                <Tooltip title={e?.name ?? ''} placement={'bottom'} arrow>
                                    <p {...dataGridCellTextProps} className={'mr-2 pr-2'}>
                                        {e?.name ?? ''}
                                    </p>
                                </Tooltip>
                            </div>,
                        copyValue: e?.name,
                    },
                    [TagManagementListDataGridColumnNames.isVisible]:
                        <Switch
                            color={'primary'}
                            checked={e.isVisibleForClient}
                            disabled={e.updatingVisibility || searchData.loading}
                            onChange={(val) => toggleTagVisibility(e, val)}
                        />,
                    [TagManagementListDataGridColumnNames.isSearchable]:
                        <Switch
                            color={'primary'}
                            checked={e.searchable}
                            disabled={e.updatingSearchable || searchData.loading}
                            onChange={(val) => toggleTagSearchAbility(e, val)}
                        />,
                    [TagManagementListDataGridColumnNames.isPublic]:
                        <Switch
                            color={'primary'}
                            checked={e.isPublic}
                            disabled={e.updatingIsPublic || searchData.loading}
                            onChange={(val) => toggleTagIsPublicStatus(e, val)}
                        />,
                    [TagManagementListDataGridColumnNames.changeIcon]:
                        <Button
                            variant={'outlined'}
                            color={'primary'}
                            disabled={searchData.loading}
                            onClick={() => setChangeTagIconDialog({
                                open: true,
                                tag: e,
                                update: (v) => setTagProperty(e.id, 'iconFileName', v),
                            })}
                        >
                            Change Icon
                        </Button>,
                    [TagManagementListDataGridColumnNames.delete]:
                        <Button
                            variant={'outlined'}
                            color={'primary'}
                            disabled={e.removing || searchData.loading}
                            onClick={() => getTagDeletionConfirmation(e)}
                        >
                            {
                                e.removing
                                    ? "Deleting..."
                                    : "Delete"
                            }
                        </Button>,
                },
            })
        })
    }, [data, industries, searchData.loading, toggleTagVisibility, toggleTagSearchAbility, toggleTagIsPublicStatus, setTagProperty, getTagDeletionConfirmation])

    return (
        <>
            <div className='tag-management-list-view'>
                <div className={'d-flex flex-row justify-content-between align-items-center w-100 mt-3 mb-2'}>
                    <p className={'view-title'}>
                        Tag Management
                    </p>
                    <Button
                        className={'d-flex justify-content-center align-items-center'}
                        variant={'outlined'}
                        color={'primary'}
                        onClick={() => setAddNewTagsDialog({open: true})}
                    >
                        <Add className='icon normal p-1 mr-1'/>
                        <p className={'button-label'}>Add new Tags</p>
                    </Button>
                </div>
                <div className={'w-100 mb-4'}>
                    <TagManagementSearchSection
                        searchData={searchData}
                    />
                </div>
                <Paper className='paper w-100 px-3 mt-5 flex-wrap justify-content-between align-items-start'>
                    <PartnerDataGrid
                        {...dataGridSaveConfig}
                        initialLayoutState={{columns: columns}}
                        rows={dataGridRows}
                        columns={dataGridColumns}
                        hideExporting
                        hideFooterActions
                        state={{
                            loading: {state: searchData.loading},
                            pagination: searchData.paginationInfo,
                            density: dataGridDensity,
                        }}
                        onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                        onPageSizeChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                    />
                </Paper>
            </div>
            <CreateNewTagsDialog
                onSuccess={addNewlyDefinedTags}
                open={addNewTagsDialog.open}
                close={() => setAddNewTagsDialog(initAddNewTagsDialog)}
            />
            <ManageTagIconDialog
                open={changeTagIconDialog.open}
                tag={changeTagIconDialog.tag}
                updateTagIconUrl={changeTagIconDialog.update}
                close={() => setChangeTagIconDialog(initChangeTagIconDialog)}
            />
        </>
    )
}

export default ProductsTagManagementListView;
